import { filterScenes, scenesByEntity, type SceneFilterMode, type StoryCtx } from '@dispositio/core';
import { useMemo } from 'react';
import { create } from 'zustand';

/** Filter über den Graphen: gewählte Elemente, UND/ODER, und das Fenster dazu (verschiebbar). Nur für diese Sitzung und Geschichte. */
interface SceneFilterState {
  /** Geschichte, zu der die Auswahl gehört (eine andere geöffnet: die Auswahl gilt nicht mehr) */
  projectId: string | null;
  entityIds: string[];
  mode: SceneFilterMode;
  open: boolean;
  /** Lage des Fensters in der Planungsfläche; null: Standardplatz oben rechts */
  pos: { x: number; y: number } | null;
  forProject: (projectId: string) => void;
  toggleOpen: () => void;
  close: () => void;
  setPos: (x: number, y: number) => void;
  toggle: (entityId: string) => void;
  setMode: (mode: SceneFilterMode) => void;
  clear: () => void;
}

export const useSceneFilter = create<SceneFilterState>((set, get) => ({
  projectId: null,
  entityIds: [],
  mode: 'and',
  open: false,
  pos: null,
  forProject: (projectId) => {
    if (get().projectId !== projectId) set({ projectId, entityIds: [], open: false, pos: null });
  },
  toggleOpen: () => set({ open: !get().open }),
  close: () => set({ open: false }),
  setPos: (x, y) => set({ pos: { x, y } }),
  toggle: (entityId) => {
    const ids = get().entityIds;
    set({ entityIds: ids.includes(entityId) ? ids.filter((i) => i !== entityId) : [...ids, entityId] });
  },
  setMode: (mode) => set({ mode }),
  clear: () => set({ entityIds: [] }),
}));

/** Index Element → Szenen, je Stand der Geschichte nur einmal berechnet (Erzähllinie, Zeichenfläche und Filterfenster teilen ihn) */
const indexCache = new WeakMap<StoryCtx, Map<string, Set<string>>>();
export function sceneIndexOf(ctx: StoryCtx): Map<string, Set<string>> {
  let index = indexCache.get(ctx);
  if (!index) {
    index = scenesByEntity(ctx);
    indexCache.set(ctx, index);
  }
  return index;
}

/** Szenen, auf die der Filter passt; null, wenn kein (noch vorhandenes) Element gewählt ist. */
export function useFilterMatch(ctx: StoryCtx | null | undefined): Set<string> | null {
  const entityIds = useSceneFilter((s) => s.entityIds);
  const mode = useSceneFilter((s) => s.mode);
  return useMemo(() => {
    if (!ctx) return null;
    const ids = entityIds.filter((id) => ctx.entities.has(id));
    return ids.length === 0 ? null : filterScenes(sceneIndexOf(ctx), ids, mode);
  }, [ctx, entityIds, mode]);
}
